import {
    ColumnType,
    DataGridTooltipedFormatter,
    getFilterElementsDefault,
    getMaskedPhone,
    getMaskFromName,
    sharpDateFormatter,
    useWorkplaceState,
    WorkplaceBtnActions,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { changeOrderItem, getOrderGeneralInfo, getOrders, getOrdersExcel } from 'axios/orderApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import Widget from 'base-components/WidgetCrm/Widget';
import DataTimeText from 'components/Common/DataTime';
import { subDays } from 'date-fns';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { OrderDetailsGeneralInfo, OrderFilter, OrderTable, OrderUpdate } from 'models/api/order';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { OrderListsProvider, useOrderLists } from 'providers/OrderListsProvider';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ImNewTab } from 'react-icons/im';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import ClientChangeModal from '../Clients/ClientChangeModal';
import './Order.scss';
import OrderChangeModal from './OrderChangeModal';
import { default as OrderFilterModal } from './OrderFilter';
import OrderCancelPopover from './Popovers/OrderCancelPopover';
import OrderChangeOperatorPopover from './Popovers/OrderChangeOperatorPopover';
import OrderChangeStatusPopover from './Popovers/OrderChangeStatusPopover';
import OrderDeletePopover from './Popovers/OrderDeletePopover';
import OrderPostponePopover from './Popovers/OrderPostponePopover';
import { dictValueFormatter } from 'utils/columnFormatters';

const OrderWorkspace = () => {
    const { t, i18n } = useTranslation();
    const { hasRole } = useAuth();
    const [isModalVisible, setModalVisible] = useState(false);
    const [clientModalProps, setClientModalProps] = useState({
        id: 0,
        visible: false,
    });

    const { dateTimeFormat, settings } = useGeneralSettingsContext();
    const { orderStatuses, loanStatuses, operators, tradeOrgs, sources, brandCodes, productTypes } =
        useOrderLists();
    const api = useApi();

    const queryClient = useQueryClient();

    const widgetIsOff =
        settings
            .find((x) => x.code === 'CrmWidget')
            ?.translations.find((x) => x.cultureCode === i18n.language)?.value ?? 'false';

    useEffect(() => {
        queryClient.invalidateQueries([['order']]);
    }, [i18n.language]);

    const initColumns = useMemo<ColumnType<OrderTable, OrderTable[]>[]>(
        () => [
            {
                key: 'id',
                columnsForSorting: [
                    {
                        key: 'id',
                        name: 'BackOfficeId',
                    },
                    { key: 'createDate', name: 'BackOfficeCreateDate' },
                ],
                name: <Trans i18nKey={'BackOfficeOrderId'} />,
                cellClass: 'normalLineHeight',
                width: '10%',
                formatter({ row }) {
                    const { id, createDate, orderType } = row;
                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={`${id} ${sharpDateFormatter(createDate, dateTimeFormat)}`}
                                overflownElementClassName="cell-ellipsis"
                            >
                                <div className="cell-ellipsis">
                                    <div className="fs-6 ">
                                        {id}
                                        {orderType ? ' ' + orderType : ''}
                                    </div>

                                    <DataTimeText value={createDate} />
                                </div>
                            </DataGridTooltipedFormatter>
                        </div>
                    );
                },
            },
            {
                key: 'Fio',
                name: <Trans i18nKey={'BackOfficeReportHeaderBeneficiaryName'} />,
                cellClass: 'normalLineHeight',
                width: '12%',
                formatter({ row }) {
                    const { fio, clientId } = row;

                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={fio}
                                overflownElementClassName="cell-ellipsis"
                            >
                                {fio ? <div className="fs-6 cell-ellipsis">{fio}</div> : <></>}
                            </DataGridTooltipedFormatter>
                            <div className="small fw-light cell-ellipsis">
                                <DataGridTooltipedFormatter
                                    tooltipText={`${t('BackOfficeClientId')} #${clientId}`}
                                    overflownElementClassName="cell-ellipsis"
                                >
                                    <div
                                        className="text-primary client-link"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setClientModalProps({ id: clientId, visible: true });
                                        }}
                                    >
                                        <Trans i18nKey={'BackOfficeClientId'} /> #{clientId}
                                    </div>
                                </DataGridTooltipedFormatter>
                            </div>
                        </div>
                    );
                },
            },
            {
                key: 'DocNum',
                name: <Trans i18nKey={'BackOfficeReportHeaderDocNum'} />,
                width: '10%',
                cellClass: 'normalLineHeight',
                formatter({ row }) {
                    const { docNum } = row;

                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={docNum}
                                overflownElementClassName="cell-ellipsis"
                            >
                                <div className="fs-6 cell-ellipsis">{docNum}</div>
                            </DataGridTooltipedFormatter>
                        </div>
                    );
                },
            },
            {
                key: 'PhoneNumber',
                name: <Trans i18nKey={'BackOfficeReportHeaderPhoneNumber'} />,
                cellClass: 'normalLineHeight',
                width: '10%',
                formatter({ row }) {
                    const { phoneNumber } = row;
                    const phoneFormatted = getMaskedPhone(
                        phoneNumber,
                        getMaskFromName('Uzbekistan')
                    );
                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={phoneFormatted}
                                overflownElementClassName="cell-ellipsis"
                            >
                                <div className="fs-6 cell-ellipsis">{phoneFormatted}</div>
                            </DataGridTooltipedFormatter>
                        </div>
                    );
                },
            },
            {
                key: 'StatusName',
                columnsForSorting: [
                    {
                        key: 'statusName',
                        name: 'BackOfficeReportHeaderStatusName',
                    },
                    { key: 'statusDt', name: 'BackOfficeReportHeaderStatusDt' },
                ],
                name: <Trans i18nKey={'BackOfficeReportHeaderStatusName'} />,
                cellClass: 'normalLineHeight',
                formatter({ row }) {
                    const { statusName, statusDt, statusDsc, statusReason } = row;
                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={
                                    <>
                                        {statusName}
                                        <br />
                                        {statusReason}
                                        <br />
                                        {statusDsc}
                                    </>
                                }
                                overflownElementClassName="cell-ellipsis"
                            >
                                <div className="cell-ellipsis">
                                    <span className="fs-6">{statusName}</span>
                                    {statusDt ? (
                                        <div className="small fw-light">
                                            <DataTimeText value={statusDt} />
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            </DataGridTooltipedFormatter>
                            <DataGridTooltipedFormatter tooltipText={statusReason}>
                                <span className="small text-danger">{statusReason}</span>
                            </DataGridTooltipedFormatter>
                            <DataGridTooltipedFormatter tooltipText={statusDsc}>
                                <span className="small fw-light">{statusDsc}</span>
                            </DataGridTooltipedFormatter>
                        </div>
                    );
                },
            },
            {
                key: 'LoanId',
                columnsForSorting: [
                    {
                        key: 'loanId',
                        name: 'BackOfficeLoan',
                    },
                    { key: 'loanStatusDt', name: 'BackOfficeLoanIssueDate' },
                    { key: 'loanStatus', name: 'BackOfficeLoanStatus' },
                ],
                name: <Trans i18nKey={'BackOfficeLoan'} />,
                cellClass: 'normalLineHeight',
                formatter({ row }) {
                    const { loanId, loanStatus, loanStatusDt } = row;

                    return (
                        <div className="pt-2">
                            <DataGridTooltipedFormatter
                                tooltipText={`${loanId} ${loanStatus} ${sharpDateFormatter(
                                    loanStatusDt,
                                    dateTimeFormat
                                )}`}
                                overflownElementClassName="cell-ellipsis"
                            >
                                <div className="cell-ellipsis">
                                    <div className="fs-6">{loanId}</div>
                                    {loanStatusDt ? (
                                        <div className="small fw-light cell-ellipsis">
                                            <DataTimeText value={loanStatusDt} />
                                        </div>
                                    ) : (
                                        <></>
                                    )}
                                    <div className="small fw-light cell-ellipsis">{loanStatus}</div>
                                </div>
                            </DataGridTooltipedFormatter>
                        </div>
                    );
                },
            },
            {
                key: 'tradeOrg',
                columnsForSorting: [
                    {
                        key: 'tradeOrgName',
                        name: 'BackOfficeTradeOrg',
                    },
                    {
                        key: 'outletName',
                        name: 'BackOfficeOutlet',
                    },
                ],
                name: <Trans i18nKey={'BackOfficeMerchant'} />,
                cellClass: 'normalLineHeight',
                formatter: ({ row: { tradeOrgName, outletName } }) =>
                    tradeOrgName && outletName ? (
                        <DataGridTooltipedFormatter
                            overflownElementClassName="text-ellipsis"
                            className="pt-2"
                        >
                            {`${t('BackOfficeTradeOrg')}: ${tradeOrgName}`}
                            <div className="font-weight-light text-ellipsis">
                                {`${t('BackOfficeOutlet')}: ${outletName}`}
                            </div>
                        </DataGridTooltipedFormatter>
                    ) : (
                        <></>
                    ),
            },
            {
                key: 'orderSource',
                name: <Trans i18nKey={'BackOfficeSource'} />,
                cellClass: 'normalLineHeight',
            },
            {
                key: 'channel',
                name: <Trans i18nKey={'BackOfficeChannel'} />,
                cellClass: 'normalLineHeight',
            },
            {
                key: 'productTypeId',
                name: <Trans i18nKey={'BackOfficeProductType'} />,
                cellClass: 'normalLineHeight',
                formatter: dictValueFormatter('productTypeId', productTypes),
            },
            {
                key: 'actions',
                name: (
                    <div className="text-center">
                        <FontAwesomeIcon icon={faBoltLightning} />
                    </div>
                ),
                cellClass: 'base-data-grid__cell_centered',
                width: '5%',
                sortable: false,
                formatter({ row }) {
                    const { id, userId, statusCode, isFinal, canEditOrder, canTransfToOper } = row;

                    return (
                        <WorkplaceBtnActions>
                            <Link
                                to={`/order/${row.id}`}
                                target="_blank"
                                type="link"
                                className="open-new-tab-link"
                                key={`openNewTab-${id}`}
                                style={{ textDecoration: 'none' }}
                            >
                                <ImNewTab className="order-action-btn__icon" />
                                <Trans i18nKey={'BackOfficeOpenInNewTab'} />
                            </Link>
                            {hasRole('orderStatusChange') && (
                                <OrderChangeStatusPopover
                                    key={`changeStatus-${id}`}
                                    orderId={`${id}`}
                                    currentStatus={statusCode}
                                />
                            )}
                            {canTransfToOper && hasRole('orderOperatorChange') && (
                                <OrderChangeOperatorPopover
                                    key={`changeOperator-${id}`}
                                    orderId={`${id}`}
                                    operatorId={userId}
                                />
                            )}
                            {!isFinal && hasRole('orderCancel') && (
                                <OrderCancelPopover
                                    orderId={`${id}`}
                                    key={`cancel-order-${id}`}
                                    currentStatus={statusCode}
                                />
                            )}
                            {canEditOrder &&
                                statusCode !== 'postpone' &&
                                hasRole('orderPostpone') && (
                                    <OrderPostponePopover
                                        orderId={`${id}`}
                                        key={`postpone-order-${id}`}
                                        currentStatus={statusCode}
                                    />
                                )}
                            {hasRole('orderDelete') && (
                                <OrderDeletePopover orderId={`${id}`} key={`delete-order-${id}`} />
                            )}
                        </WorkplaceBtnActions>
                    );
                },
            },
        ],
        [dateTimeFormat, t, productTypes, setClientModalProps]
    );

    return (
        <>
            {widgetIsOff && widgetIsOff !== 'false' && <Widget />}

            <BOWorkplace<OrderTable, OrderDetailsGeneralInfo, OrderFilter, OrderUpdate>
                pageTitle="BackOfficeOrders"
                defaultPageSize={50}
                filterParams={getDefaultOrderFilter()}
                initColumns={initColumns}
                storagePath="orderStatus/columns"
                rowClass={(row) => (row.isRepeatClient ? '' : 'new-client-row')}
                lineHeight={70}
                fetchExcelQueryFn={(filter) => getOrdersExcel(api, filter)}
                fetchQueryFn={(filter) => getOrders(api, filter)}
                fetchFormat={(dataObject) => dataObject}
                getFilterElements={getFilterElementsDefault([
                    {
                        id: 'sd',
                        formatter: (val) =>
                            `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                    },
                    {
                        id: 'ed',
                        formatter: (val) =>
                            `${t('BackOfficeTo')}: ${sharpDateFormatter(val, dateTimeFormat)}`,
                    },
                    { name: t('BackOfficeLoanId'), id: 'loanId' },
                    { name: t('BackOfficeReportHeaderOrderId'), id: 'orderId' },
                    {
                        name: t('BackOfficeMobilePhone'),
                        id: 'phone',
                        formatter: (val) =>
                            getMaskedPhone(val.toString(), getMaskFromName('Uzbekistan')),
                    },
                    { name: t('BackOfficeReportHeaderDocNum'), id: 'docNum' },
                    {
                        id: 'orderStatuses',
                        name: t('BackOfficeOrderStatuses'),
                        formatter: (val) =>
                            val
                                .map((x) => orderStatuses.find((s) => s.id === x)?.name || x)
                                .join(', '),
                    },
                    {
                        name: t('BackOfficeLoanStatuses'),
                        id: 'loanStatuses',
                        formatter: (val) =>
                            val
                                .map((x) => loanStatuses.find((s) => s.id === x)?.name || x)
                                .join(', '),
                    },
                    { name: t('BackOfficeReportHeaderBeneficiaryName'), id: 'fio' },
                    {
                        name: t('BackOfficeOperators'),
                        id: 'operators',
                        formatter: (val) =>
                            val
                                .map((x) => {
                                    const user = operators.find((u) => u.id === x);
                                    return user ? user.lastName + ' ' + user.firstName : x;
                                })
                                .join(', '),
                    },
                    {
                        name: t('BackOfficeTradeOrg'),
                        id: 'tradeOrgId',
                        formatter: (val) =>
                            tradeOrgs.find((o) => o.id === val)?.name ?? val.toString(),
                    },
                    {
                        id: 'orderSource',
                        name: t('BackOfficeSource'),
                        formatter: (val) =>
                            sources?.find((t) => t.id === val)?.name ?? val.toString(),
                    },
                    {
                        id: 'productTypeId',
                        name: t('BackOfficeProductType'),
                        formatter: (val) => productTypes?.find((t) => t.id === val)?.name ?? val,
                    },
                    {
                        id: 'brandCode',
                        name: t('BackOfficeBrandCode'),
                        formatter: (val) =>
                            brandCodes?.find((o) => o.id === val)?.name ?? val.toString(),
                    },
                ])}
                showModal={() => setModalVisible(true)}
                filter={<OrderFilterModal />}
                modal={{
                    component: isModalVisible ? (
                        <OrderChangeModal
                            isOpen={isModalVisible}
                            toggle={() => setModalVisible(false)}
                        />
                    ) : (
                        <></>
                    ),
                    fetchCurrentItemQueryFn: (id) => getOrderGeneralInfo(api, id),
                    changeMutationFn: (item) => changeOrderItem(api, item),
                    addMutationFn: (item) => {
                        return new Promise<OrderDetailsGeneralInfo>((resolve) => resolve(item));
                    },
                }}
                queryKey={['order']}
                queryClient={queryClient}
            />
            {clientModalProps.visible && (
                <ClientChangeModal
                    isOpen={clientModalProps.visible}
                    toggle={(value) => {
                        setClientModalProps((prev) => ({ ...prev, visible: !!value.valueOf() }));
                    }}
                    id={clientModalProps.id}
                />
            )}
        </>
    );
};

const OrderWorkspaceWrapped = (props: any) => {
    return (
        <OrderListsProvider>
            <OrderWorkspace {...props} />
        </OrderListsProvider>
    );
};

const getDefaultOrderFilter = () => ({
    sd: getStartOfTheDay(subDays(new Date(), 7)),
    ed: getEndOfTheDay(),
    loanId: '',
    orderId: '',
    orderSettingBasketType: '',
    creditProduct: '',
    orderStatuses: { value: [], array: true },
    loanStatuses: { value: [], array: true },
    operators: { value: [], array: true },
    phone: '',
    docNum: '',
    fio: '',
    orderType: '',
    isIgnorePeriod: false,
    tradeOrgId: null,
    brandCode: null,
    outletId: null,
});

const useOrderWorkplaceState = () =>
    useWorkplaceState<OrderTable, OrderDetailsGeneralInfo, OrderUpdate>();

export default OrderWorkspaceWrapped;
export { useOrderWorkplaceState, getDefaultOrderFilter };
